import { playerNameFor } from '@/composables/api/usePlayerSummaries';
import type { ZoneLookup } from '@/composables/api/useZoneSummaries';
import { customListScheduleFieldOf } from '@/components/FilterBuilder/filterControlRegistry';
import { idLookupDisplayString } from '@/constants/lookupFilterOptions';
import type { SortDirection } from '@/types/tableTypes';
import {
    DAY_IDS,
    SCHEDULE_KIND_LABELS,
    SCHEDULE_ROLE_LABELS,
    decodeDayIds,
    formatClockTime,
    formatDayNames,
    formatScheduleShortDate,
    isRecurringRow,
    normalizeClockTime,
    parseWallClock,
    scheduleRole,
    scheduleTimes,
    scheduleWindow,
    summarizeSchedule,
    summaryText,
    toBool,
    type BoolLike,
    type ScheduleRole,
    type ScheduleRow,
    type ScheduleState,
    type ScheduleSummary,
} from '@/utils/customPlaylistSchedule';

/**
 * The grouping behind the Schedules drawer and the Schedules inspector section
 * (decision D2). Pure: the caller passes the zone lookup and `now`, so the
 * rules can be tested without Vue or a server.
 *
 * A custom list's schedules can come from several zones. They are grouped by
 * the zone that wrote them:
 *
 *   1. the list's own zone, labelled "Owner", always first;
 *   2. subscribers of that zone, by zone name, labelled "Subscriber";
 *   3. any other zone, by zone name, labelled "Other zone";
 *   4. zones the app cannot place: the lookup has not answered yet (no tag,
 *      just the name), or it answered without the zone, because it is deleted
 *      or hidden from this user (tagged "Unknown zone", named "Zone #<id>").
 *      Neither is called "Other zone": that would claim the zone is not a
 *      subscriber, which the app does not know (amendment A8).
 *
 * Inside a group the schedules run in time order.
 */

/** Which schedules a tab shows. */
export type ScheduleTab = 'in-force' | 'upcoming' | 'expired' | 'all';

export const SCHEDULE_TAB_LABELS: Readonly<Record<ScheduleTab, string>> = {
    'in-force': 'Active',
    upcoming: 'Upcoming',
    expired: 'Expired',
    all: 'All',
};

/** Past this many groups, the rest start collapsed to their header line. */
export const EXPANDED_GROUP_LIMIT = 5;

export interface ScheduleEntry {
    schedule: ScheduleRow;
    summary: ScheduleSummary;
    /** One line of plain text, used for the filter and for titles. */
    text: string;
    /** A stable key for v-for. */
    key: string;
}

export interface ScheduleGroup {
    /** "zone-<id>", or "zone-none" for schedules that carry no zone. */
    key: string;
    zoneId: number | null;
    /** null when the app cannot place the zone: the lookup is pending, or could not find it. */
    role: ScheduleRole | null;
    /** "Owner", "Subscriber", "Other zone", "Unknown zone" when the lookup could not find it, or "" (no tag) while pending. */
    roleLabel: string;
    /** The zone's name, or "Zone #<id>" when the lookup cannot name it. */
    zoneName: string;
    /** The zone's master id when known (for "no master" style hints). */
    masterLocationZoneId: number | null | undefined;
    entries: ScheduleEntry[];
}

export interface ScheduleTabCounts {
    'in-force': number;
    upcoming: number;
    expired: number;
    all: number;
}

export interface ScheduleGroupContext {
    /** The list's own `location_zone_id`. */
    ownerZoneId: number | null;
    /** The owner zone's name from the list document, when it carries one. */
    ownerZoneName?: string | null;
    /** Reads the zone cache. May queue a lookup as a side effect. */
    lookupZone: (zoneId: number) => ZoneLookup;
    /** The owner zone's wall-clock time (amendment A6). */
    now: Date;
}

export function positiveZoneId(value: unknown): number | null {
    const id = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

export function asScheduleRows(items: unknown): ScheduleRow[] {
    return Array.isArray(items)
        ? (items.filter((item) => !!item && typeof item === 'object') as ScheduleRow[])
        : [];
}

/**
 * Summaries for every schedule, computed once per render. A station change
 * names its player through the batched player lookup, which only a list that
 * has a station change ever asks.
 */
export function toEntries(
    schedules: ScheduleRow[],
    now: Date,
    playerName: (locationZonePlayerId: number) => string | null | undefined = playerNameFor,
    /** Each schedule's own zone clock, when the caller knows it (see scheduleZoneNow). */
    nowFor?: (schedule: ScheduleRow) => Date,
): ScheduleEntry[] {
    return schedules.map((schedule, index) => {
        const summary = summarizeSchedule(schedule, { now: nowFor ? nowFor(schedule) : now, playerName });
        return {
            schedule,
            summary,
            text: summaryText(summary),
            key: schedule.id != null ? String(schedule.id) : `index-${index}`,
        };
    });
}

export function tabCounts(entries: ScheduleEntry[]): ScheduleTabCounts {
    const counts: ScheduleTabCounts = { 'in-force': 0, upcoming: 0, expired: 0, all: entries.length };
    for (const entry of entries) {
        counts[entry.summary.state] += 1;
    }
    return counts;
}

export function entryMatchesTab(entry: ScheduleEntry, tab: ScheduleTab): boolean {
    return tab === 'all' || entry.summary.state === tab;
}

/**
 * The tab to open on. Active, unless nothing is active: two thirds of all
 * schedules are expired and most lists carry exactly one, so a drawer that
 * always opened on "Active" would usually open empty. Then the first tab
 * that has something in it.
 */
export function defaultTab(counts: ScheduleTabCounts): ScheduleTab {
    if (counts['in-force'] > 0) return 'in-force';
    if (counts.upcoming > 0) return 'upcoming';
    return counts.all > 0 ? 'all' : 'in-force';
}

/** Sort key inside a group: window start (open start first), then time of day, then id. */
function compareEntries(left: ScheduleEntry, right: ScheduleEntry): number {
    const leftStart = scheduleWindow(left.schedule).start?.getTime() ?? Number.NEGATIVE_INFINITY;
    const rightStart = scheduleWindow(right.schedule).start?.getTime() ?? Number.NEGATIVE_INFINITY;
    if (leftStart !== rightStart) return leftStart < rightStart ? -1 : 1;
    const leftTime = scheduleTimes(left.schedule).start ?? '';
    const rightTime = scheduleTimes(right.schedule).start ?? '';
    if (leftTime !== rightTime) return leftTime < rightTime ? -1 : 1;
    return Number(left.schedule.id ?? 0) - Number(right.schedule.id ?? 0);
}

const ROLE_ORDER: Record<string, number> = { owner: 0, subscriber: 1, other: 2, unresolved: 3 };

/** The lookup answered and the zone was not in it. */
const UNKNOWN_ZONE_LABEL = 'Unknown zone';

function zoneLabel(zoneId: number | null): string {
    return zoneId === null ? 'No zone' : `Zone #${zoneId}`;
}

/**
 * Group every entry by the zone that wrote it. Groups are built from ALL the
 * entries (so a zone keeps its place when a tab or filter empties it) and the
 * caller narrows them with {@link filterGroups}.
 */
export function buildScheduleGroups(entries: ScheduleEntry[], context: ScheduleGroupContext): ScheduleGroup[] {
    const byZone = new Map<string, ScheduleGroup>();
    for (const entry of entries) {
        const zoneId = positiveZoneId(entry.schedule.location_zone_id);
        const key = zoneId === null ? 'zone-none' : `zone-${zoneId}`;
        let group = byZone.get(key);
        if (!group) {
            const isOwner = zoneId !== null && zoneId === context.ownerZoneId;
            const lookup: ZoneLookup = zoneId === null ? undefined : context.lookupZone(zoneId);
            const masterLocationZoneId = lookup === undefined ? undefined : lookup === null ? null : lookup.masterLocationZoneId;
            // A zone the lookup could not find has no known master: keep it unplaced
            // (role null) rather than let a null master read as "Other zone".
            const role = isOwner
                ? 'owner'
                : zoneId === null
                  ? 'other'
                  : lookup === null
                    ? null
                    : scheduleRole(entry.schedule, { ownerZoneId: context.ownerZoneId, zoneMasterId: masterLocationZoneId });
            const ownerName = isOwner ? context.ownerZoneName?.trim() || null : null;
            const lookedUpName = lookup ? lookup.displayString : null;
            group = {
                key,
                zoneId,
                role,
                roleLabel: role !== null ? SCHEDULE_ROLE_LABELS[role] : lookup === null ? UNKNOWN_ZONE_LABEL : '',
                zoneName: ownerName ?? lookedUpName ?? zoneLabel(zoneId),
                masterLocationZoneId,
                entries: [],
            };
            byZone.set(key, group);
        }
        group.entries.push(entry);
    }

    const groups = [...byZone.values()];
    for (const group of groups) {
        group.entries.sort(compareEntries);
    }
    return groups.sort((left, right) => {
        const byRole = ROLE_ORDER[left.role ?? 'unresolved']! - ROLE_ORDER[right.role ?? 'unresolved']!;
        if (byRole !== 0) return byRole;
        const byName = left.zoneName.localeCompare(right.zoneName, undefined, { sensitivity: 'base', numeric: true });
        if (byName !== 0) return byName;
        return (left.zoneId ?? 0) - (right.zoneId ?? 0);
    });
}

/** The day ids a filter word names: "mon", "monday", "tu", "sun"… (two letters at least). */
function daysNamedBy(word: string): number[] {
    if (word.length < 2) return [];
    return DAY_IDS.filter((day) => day.name.toLowerCase().startsWith(word)).map((day) => day.id);
}

/**
 * Whether one schedule matches the filter text. Every word must match
 * something: the zone's name or id, the role label, the schedule's own text,
 * its type, or — for a repeating schedule — one of the days it runs on
 * ("fri" matches an every-day schedule and an M W F one, not a Tu Th one;
 * "daily" matches an every-day schedule).
 */
export function entryMatchesFilter(entry: ScheduleEntry, group: ScheduleGroup, filter: string): boolean {
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return true;
    const haystack = [
        group.zoneName,
        group.zoneId !== null ? String(group.zoneId) : '',
        group.roleLabel,
        entry.text,
        SCHEDULE_KIND_LABELS[entry.summary.kind],
        entry.summary.dateWindow ?? '',
        // Shown as "Every day", but "daily" is what people type.
        entry.summary.days === 'daily' ? 'daily' : '',
    ]
        .join(' ')
        .toLowerCase();
    const runsOn = isRecurringRow(entry.schedule) ? decodeDayIds(entry.schedule.delimited_day_ids) : [];
    return words.every((word) => {
        if (haystack.includes(word)) return true;
        const named = daysNamedBy(word);
        return named.length > 0 && named.some((dayId) => runsOn.includes(dayId));
    });
}

/** The groups narrowed to one tab and the filter text; empty groups drop out. */
export function filterGroups(groups: ScheduleGroup[], tab: ScheduleTab, filter: string): ScheduleGroup[] {
    return groups
        .map((group) => ({
            ...group,
            entries: group.entries.filter(
                (entry) => entryMatchesTab(entry, tab) && entryMatchesFilter(entry, group, filter),
            ),
        }))
        .filter((group) => group.entries.length > 0);
}

const STATE_RANK: Record<ScheduleState, number> = { 'in-force': 0, upcoming: 1, expired: 2 };

/**
 * The order of the schedules on a search card, ranked by what a person is
 * looking for: in-force schedules first, the list's own zone's before the
 * others, then upcoming ones by start, then expired ones with the most recent
 * end first. The card draws its lines in this order, and the table cell draws
 * its one-line cards in it too.
 */
export function sortCardSchedules(entries: ScheduleEntry[], ownerZoneId: number | null): ScheduleEntry[] {
    const isOwner = (entry: ScheduleEntry) =>
        ownerZoneId !== null && positiveZoneId(entry.schedule.location_zone_id) === ownerZoneId;
    return [...entries].sort((left, right) => {
        const byState = STATE_RANK[left.summary.state] - STATE_RANK[right.summary.state];
        if (byState !== 0) return byState;
        if (left.summary.state === 'in-force') {
            const byOwner = Number(isOwner(right)) - Number(isOwner(left));
            if (byOwner !== 0) return byOwner;
            return compareEntries(left, right);
        }
        if (left.summary.state === 'upcoming') {
            return compareEntries(left, right);
        }
        // Expired: the most recent end first.
        const leftEnd = scheduleWindow(left.schedule).end?.getTime() ?? 0;
        const rightEnd = scheduleWindow(right.schedule).end?.getTime() ?? 0;
        return rightEnd - leftEnd;
    });
}

/** What a schedule column's cell shows for a schedule that leaves the field empty. */
export const EMPTY_SCHEDULE_FIELD_TEXT = '–';

const EFFECTIVE_DATETIME_FIELDS: ReadonlySet<string> = new Set(['effective_start_datetime', 'effective_end_datetime']);
const TIME_FIELDS: ReadonlySet<string> = new Set(['time', 'end_time']);
const NUMBER_FIELDS: ReadonlySet<string> = new Set(['custom_playlist_schedule_type_id', 'location_zone_id']);

function isEmptyValue(value: unknown): boolean {
    return value === null || value === undefined || value === '';
}

/**
 * The value the search sorts a schedule field by, or null when the schedule leaves it
 * empty. Each one sorts the way the backend sorts it. Dates and the day string compare
 * as their stored text, so "1,3,5" comes before "2,4". Times compare by time of day,
 * the effective date-times by when they are, and the type and zone by number.
 */
function sortValueOf(schedule: ScheduleRow, field: string): string | number | null {
    const value = (schedule as Record<string, unknown>)[field];
    if (isEmptyValue(value)) return null;
    if (NUMBER_FIELDS.has(field)) {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
    }
    if (EFFECTIVE_DATETIME_FIELDS.has(field)) {
        return parseWallClock(value as string | Date)?.getTime() ?? null;
    }
    if (TIME_FIELDS.has(field)) {
        return normalizeClockTime(String(value));
    }
    return String(value);
}

/**
 * The schedule lines of a table row, in the order of the sort the rows were searched
 * with (rule 8 of docs/specs/custom-list-schedule-columns.md in tabasco).
 *
 * Only the first schedule column in the sort decides. Sorted by "Status, then Date,
 * then End Date", the lines follow Date. Ascending puts the smallest value on line 1,
 * descending the largest. A schedule that leaves the field empty goes last both ways.
 * Schedules with the same value keep the order they came in, which is the card's order:
 * running now, then upcoming, then expired. So line 1 of the sorted column is the value
 * that placed the list.
 *
 * A sort with no schedule column gives the entries back as they came.
 */
export function orderBySchedulesSort(
    entries: ScheduleEntry[],
    sort: Readonly<Record<string, SortDirection>> | null | undefined,
): ScheduleEntry[] {
    const scheduleSort = Object.entries(sort ?? {}).find(
        ([key, direction]) => customListScheduleFieldOf(key) !== null && (direction === 'asc' || direction === 'desc'),
    );
    if (!scheduleSort) return entries;
    const field = customListScheduleFieldOf(scheduleSort[0])!;
    const sign = scheduleSort[1] === 'desc' ? -1 : 1;
    // Each value is worked out once, not once per comparison. A list can hold hundreds of
    // schedules, and an effective date-time is parsed to get its value. Equal values fall
    // back to the position they came in, so they keep the card order.
    const decorated = entries.map((entry, index) => ({ entry, index, value: sortValueOf(entry.schedule, field) }));
    decorated.sort((left, right) => {
        if (left.value !== right.value) {
            if (left.value === null) return 1;
            if (right.value === null) return -1;
            return (left.value < right.value ? -1 : 1) * sign;
        }
        return left.index - right.index;
    });
    return decorated.map(({ entry }) => entry);
}

/** "2026-11-03" from a wall-clock Date. */
function calendarDateOf(date: Date): string {
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((part) => String(part).padStart(2, '0')).join('-');
}

/** "06:30:00" from a wall-clock Date. */
function clockTimeOf(date: Date): string {
    return [date.getHours(), date.getMinutes(), date.getSeconds()].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * One schedule field as a schedule column's cell shows it, or a dash when the schedule
 * leaves it empty. Dates read "3 Nov 2026". The effective date-times read
 * "3 Nov 2026 6:30am". Times read "6am". Days read "Mon Wed Fri",
 * from the stored ids, where Sunday is 1. Schedule Type reads the type's name, such as
 * "Recurring". Zone ID reads the number. Silence and On Other Zone read "Yes" or "No".
 */
export function scheduleFieldText(schedule: ScheduleRow, field: string): string {
    const value = (schedule as Record<string, unknown>)[field];
    if (isEmptyValue(value)) return EMPTY_SCHEDULE_FIELD_TEXT;
    if (EFFECTIVE_DATETIME_FIELDS.has(field)) {
        const wallClock = parseWallClock(value as string | Date);
        if (!wallClock) return String(value);
        return [formatScheduleShortDate(calendarDateOf(wallClock)), formatClockTime(clockTimeOf(wallClock))]
            .filter(Boolean)
            .join(' ');
    }
    if (TIME_FIELDS.has(field)) {
        return formatClockTime(String(value)) ?? String(value);
    }
    if (field === 'delimited_day_ids') {
        const dayIds = decodeDayIds(String(value));
        if (dayIds.length === 0) return EMPTY_SCHEDULE_FIELD_TEXT;
        return formatDayNames(dayIds.length === DAY_IDS.length ? 'daily' : dayIds);
    }
    if (field === 'custom_playlist_schedule_type_id') {
        return idLookupDisplayString('custom_playlist_schedule_type', Number(value)) ?? String(value);
    }
    if (field === 'location_zone_id') {
        return String(value);
    }
    if (field === 'silence') {
        return toBool(value as BoolLike) ? 'Yes' : 'No';
    }
    if (field === 'is_on_other_zone') {
        return value === true ? 'Yes' : 'No';
    }
    return formatScheduleShortDate(String(value)) ?? String(value);
}
